import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import { PaginationControls } from 'Angular/features/components/pagination/pagination.constants';
import { PaginationInfo } from '../interfaces/pagination-info.interface';

/**
 * Pagination Controls Component
 *
 * This component provides generic functionality for pagination controls/buttons that are commonly used in components
 * that display large lists of items.
 *
 * Input Properties
 *  disableButtons: boolean used to control whether or not the pagination buttons should be clickable
 *  paginationInfo: PaginationInfo object used for computing the new page when a button is clicked
 *
 * Output Properties
 *  pageChangeHandler: EventEmitter that emits the new page number after the page has been changed
 *
 */
@Component({
    standalone: false,
    selector: 'ycn-pagination-controls',
    templateUrl: './pagination-controls.template.html',
})
export class PaginationControlsComponent implements OnInit, OnChanges {
    // If this input is true, then all buttons in this component will be disabled
    @Input() disableButtons: boolean = false;

    @Input() paginationInfo!: PaginationInfo;

    @Output() pageChangeHandler = new EventEmitter<number>();

    PAGE_CONTROLS = PaginationControls;

    pageNum: number = 1;

    isInitialState: boolean = true;

    ngOnInit(): void {
        if (this.paginationInfo) {
            this.pageNum = this.paginationInfo.current_page_num;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.paginationInfo?.currentValue) {
            this.pageNum = changes.paginationInfo.currentValue.current_page_num;
            this.isInitialState = this.isInitialPaginationState(changes.paginationInfo.currentValue);
        }
    }

    isInitialPaginationState(paginationInfo: PaginationInfo): boolean {
        // Check if all values are zeros in pagination info
        return Object.values(paginationInfo).every((value) => value === 0);
    }

    /**
     * Uses the current value of pageNum and the paginationEvent to determine the next page number.
     * @param paginationEvent the pagination button that was clicked
     */
    pageChanged(paginationEvent: PaginationControls): void {
        // skip `first` or `prev` if already on the first page.
        if (
            (paginationEvent === this.PAGE_CONTROLS.FIRST || paginationEvent === this.PAGE_CONTROLS.PREV) &&
            this.pageNum === 1
        )
            return;
        // skip `last` or `next` if already on the last page.
        if (
            (paginationEvent === this.PAGE_CONTROLS.LAST || paginationEvent === this.PAGE_CONTROLS.NEXT) &&
            this.pageNum >= this.paginationInfo.num_pages
        )
            return;

        let newPageNum;
        switch (paginationEvent) {
            case this.PAGE_CONTROLS.FIRST:
                newPageNum = 1;
                break;
            case this.PAGE_CONTROLS.PREV:
                newPageNum = Math.max(this.pageNum - 1, 1);
                break;
            case this.PAGE_CONTROLS.NEXT:
                newPageNum = Math.min(this.pageNum + 1, this.paginationInfo.num_pages);
                break;
            case this.PAGE_CONTROLS.LAST:
                newPageNum = this.paginationInfo.num_pages;
                break;
            default:
                newPageNum = 1;
        }
        this.pageNum = newPageNum;
        this.pageChangeHandler.emit(this.pageNum);
    }
}
